import { State } from '@progress/kendo-data-query';
import { useCallback, useState } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import {
  CheckbookLineItemError,
  CheckbookLineItemInfo,
  CheckbookLineItemWarning,
} from 'src/sections/view_checkbook/useCheckbookPeriodErrors';
import { round } from 'src/utils';
import axiosInstance from 'src/utils/axios';
import { modifyRequest } from 'src/utils/requestInterceptor';

export type Card = 'subtract' | 'add';

export interface GLCodesBudgetResponseObject {
  id: number;
  code: string;
  description: string;
  remainingBudgetAmount: number;
  budgetAmount: number;
  holdAmount: number;
  spentAmount: number;
  isActive: boolean;
  isDeleted?: boolean;
  hasHoldTransactionData?: boolean;
  hasSpentTransactionData?: boolean;
  remainingBudgetAmountPercentage: number;
  selected: boolean;
  isNewGlCode?: boolean;
  rowId: string;
  errors?: CheckbookLineItemError;
  warnings?: CheckbookLineItemWarning;
  informations?: CheckbookLineItemInfo;
  hasErrorsOrWarnings?: boolean;
  card: Card;
  newAmount: number;
}

export type GLCodesBudgets = {
  totalRecords: number;
  items: GLCodesBudgetResponseObject[];
};

export interface IGLCodesBudgetApiProps {
  glPeriodId?: number;
  checkbookDataState?: State;
  clientSide?: boolean;
}

const useGLCodesBudgetApi = (companyId: string) => {
  const [glCodesBudget, setGlCodesBudget] = useState<GLCodesBudgetResponseObject[]>([]);
  const call = useCallback(
    async (params?: IGLCodesBudgetApiProps) => {
      const { glPeriodId, checkbookDataState = {}, clientSide = false } = params ?? {};
      try {
        const res = await axiosInstance.post<GLCodesBudgets>(
          api.glCodesBudget(companyId, glPeriodId),
          { ...modifyRequest(checkbookDataState), clientSide }
        );
        const { items, totalRecords } = res.data;
        const data: GLCodesBudgetResponseObject[] = items?.map((item) => ({
          ...item,
          remainingBudgetAmountPercentage: round(
            (item.remainingBudgetAmount / item.budgetAmount) * 100,
            2
          ),
          selected: false,
          rowId: uuid(),
          card: 'subtract',
          newAmount: 0,
        }));
        setGlCodesBudget(data);
        return { totalRecords, items: data };
      } catch (error) {
        throw new Error(error);
      }
    },
    [companyId]
  );
  const {
    isLoading,
    value: { totalRecords = 0 },
    execute,
  } = useAsync(call, false, undefined, {} as GLCodesBudgets);

  return {
    isLoading,
    glCodesBudget,
    setGlCodesBudget,
    totalGlCodesBudget: totalRecords,
    getGlCodesBudget: execute,
  };
};

export default useGLCodesBudgetApi;
